import React, { useEffect, useState, useCallback } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './PublicHeader.scss';

// Material UI components para el menú móvil
import { Chip, Collapse, List, ListItem } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

// Configuración de secciones para la navegación
const SECCIONES_NAVEGACION = [
  { id: 'introduction', label: 'Inicio' },
  { id: 'servicios', label: 'Servicios' },
  { id: 'social', label: 'Social' },
  { id: 'aliados', label: 'Aliados' },
  { id: 'contacto', label: 'Contacto' }
];

const PublicHeader = () => {
  // Estados del componente
  const [seccionActiva, setSeccionActiva] = useState('introduction');
  const [headerComprimido, setHeaderComprimido] = useState(false);
  const [menuMovilAbierto, setMenuMovilAbierto] = useState(false);
  const [esPantallaPequena, setEsPantallaPequena] = useState(window.innerWidth < 768);

  // Hooks de React Router para gestión de rutas
  const location = useLocation();
  const navigate = useNavigate();

  // Verificar si estamos en la página principal (home/landing)
  const esPaginaPrincipal = location.pathname === '/';

  // Detectar cambios de tamaño de pantalla para responsive design
  useEffect(() => {
    const manejarRedimensionamiento = () => {
      const esPequena = window.innerWidth < 768;
      setEsPantallaPequena(esPequena);
      
      if (!esPequena) {
        setMenuMovilAbierto(false);
      }
    };

    window.addEventListener('resize', manejarRedimensionamiento);
    return () => window.removeEventListener('resize', manejarRedimensionamiento);
  }, []);

  // Gestión de cambios de ruta - resetear estado cuando no estamos en home
  useEffect(() => {
    if (!esPaginaPrincipal) {
      // Si no estamos en la página principal, resetear el estado
      setSeccionActiva('');
      setHeaderComprimido(true);
    } else {
      // Si volvemos a la página principal, restaurar estado inicial
      setSeccionActiva('introduction');
      setHeaderComprimido(false);
    }
  }, [location.pathname, esPaginaPrincipal]);

  // Listener mejorado para cambios de sección (solo activo en página principal)
  useEffect(() => {
    if (!esPaginaPrincipal) return;

    const manejarCambioSeccion = (event) => {
      const nuevaSeccion = event.detail.seccion;
      setSeccionActiva(nuevaSeccion);
      
      // Comprimir header con transición suave
      const debeComprimir = nuevaSeccion !== 'introduction';
      setHeaderComprimido(debeComprimir);
    };

    window.addEventListener('seccionCambiada', manejarCambioSeccion);
    
    return () => {
      window.removeEventListener('seccionCambiada', manejarCambioSeccion);
    };
  }, [esPaginaPrincipal]);

  // Función mejorada para navegar a una sección específica
  const navegarASeccion = useCallback((seccionId) => {
    setMenuMovilAbierto(false);
    
    // Si no estamos en la página principal, navegar primero a home
    if (!esPaginaPrincipal) {
      navigate('/');
      // Esperar a que React Router procese la navegación
      setTimeout(() => {
        // Después de navegar a home, disparar el evento de navegación a sección
        window.dispatchEvent(new CustomEvent('navegarASeccion', { 
          detail: { seccion: seccionId } 
        }));
      }, 100);
    } else {
      // Si ya estamos en home, navegar directamente a la sección
      if (seccionId === seccionActiva) return;
      
      setSeccionActiva(seccionId);
      setHeaderComprimido(seccionId !== 'introduction');
      
      window.dispatchEvent(new CustomEvent('navegarASeccion', { 
        detail: { seccion: seccionId } 
      }));
    }
  }, [seccionActiva, esPaginaPrincipal, navigate]);

  // Obtener etiqueta de la sección activa para display móvil
  const obtenerEtiquetaSeccionActiva = () => {
    if (!esPaginaPrincipal) {
      // Si no estamos en home, mostrar el nombre de la ruta actual
      const rutaActual = location.pathname;
      switch (rutaActual) {
        case '/login':
          return 'Iniciar Sesión';
        case '/register':
          return 'Registro';
        default:
          return 'Navegación';
      }
    }

    const seccion = SECCIONES_NAVEGACION.find(s => s.id === seccionActiva);
    return seccion ? seccion.label : 'Inicio';
  };

  // Función para manejar click en el logo (siempre va a home/introduction)
  const manejarClickLogo = () => {
    if (!esPaginaPrincipal) {
      navigate('/');
    } else {
      navegarASeccion('introduction');
    }
  };

  // Variantes de animación para el header
  const variantesHeader = {
    normal: {
      height: '80px',
      transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }
    },
    comprimido: {
      height: '60px',
      transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }
    }
  };

  // Variantes para el lema
  const variantesLema = {
    visible: {
      opacity: 1,
      y: 0,
      height: 'auto',
      transition: { duration: 0.4, ease: 'easeOut' }
    },
    oculto: {
      opacity: 0,
      y: -10,
      height: 0,
      transition: { duration: 0.3, ease: 'easeIn' }
    }
  };

  return (
    <motion.header 
      className="public-header"
      variants={variantesHeader}
      animate={headerComprimido ? 'comprimido' : 'normal'}
    >
      <div className="header-container">
        {/* Logo y lema con nuevo diseño */}
        <div className="brand-section">
          <motion.div 
            className="logo-texto"
            onClick={manejarClickLogo}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <h1 className="nombre-empresa">
              <span className="cootrans">Cootrans</span>
              <span className="petrols">Petrols</span>
            </h1>
            
            {/* Lema que aparece/desaparece según la sección */}
            <AnimatePresence>
              {!headerComprimido && esPaginaPrincipal && (
                <motion.p 
                  className="lema-empresa"
                  variants={variantesLema}
                  initial="oculto"
                  animate="visible"
                  exit="oculto"
                >
                  Energía sobre ruedas
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Navegación de escritorio con nuevo indicador */}
        <nav className="navegacion-desktop">
          <ul className="lista-navegacion">
            {SECCIONES_NAVEGACION.map((seccion) => (
              <li key={seccion.id}>
                <motion.button
                  className={`enlace-navegacion ${
                    esPaginaPrincipal && seccionActiva === seccion.id ? 'activo' : ''
                  }`}
                  onClick={() => navegarASeccion(seccion.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {seccion.label}
                  
                  {/* Nuevo indicador de línea */}
                  {esPaginaPrincipal && seccionActiva === seccion.id && (
                    <motion.div 
                      className="indicador-linea"
                      layoutId="indicadorLinea"
                      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    />
                  )}
                </motion.button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Navegación móvil */}
        <div className="navegacion-movil">
          <Chip
            label={obtenerEtiquetaSeccionActiva()}
            onClick={() => setMenuMovilAbierto(!menuMovilAbierto)}
            onDelete={() => setMenuMovilAbierto(!menuMovilAbierto)}
            deleteIcon={menuMovilAbierto ? <ExpandLess /> : <ExpandMore />}
            color="primary"
            className="chip-seccion"
          />
          
          <Collapse in={menuMovilAbierto} timeout="auto" unmountOnExit>
            <div className="contenedor-menu-movil">
              <List component="nav" className="menu-movil">
                {SECCIONES_NAVEGACION.map((seccion) => (
                  <ListItem 
                    button 
                    key={seccion.id}
                    onClick={() => navegarASeccion(seccion.id)}
                    className={
                      esPaginaPrincipal && seccionActiva === seccion.id ? 'item-activo' : ''
                    }
                  >
                    {seccion.label}
                  </ListItem>
                ))}
              </List>
            </div>
          </Collapse>
        </div>

        {/* Botón de login actualizado */}
        <div className="acciones-auth">
          <RouterLink to="/login" className="boton-login">
            Ingresar
          </RouterLink>
        </div>
      </div>
    </motion.header>
  );
};

export default PublicHeader;